import { useState, useCallback, useRef } from 'react';
import { Wand2, RotateCcw } from 'lucide-react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { rankAllFeatureBundles } from '../../lib/calculations';
import { FEATURES } from '../../data/conjointData';
import { InfoButton } from '../common/Tooltip';
import { formatPrice } from '../../lib/formatters';

interface TierSuggestion {
  price: number;
  features: string[];
}

function computeSuggestions(
  tierCount: number,
  assumptions: ReturnType<typeof useSimulatorStore.getState>['assumptions']
): TierSuggestion[] {
  const pricesByTier = tierCount === 1
    ? [1499]
    : tierCount === 2
      ? [1499, 499]
      : tierCount === 3
        ? [1499, 999, 499]
        : tierCount === 4
          ? [1899, 1499, 999, 499]
          : [1899, 1699, 1499, 999, 499];

  const sortedFeatures = [...FEATURES].sort((a, b) => b.lift - a.lift).map(f => f.name);

  return pricesByTier.map((price, i) => {
    const bundles = rankAllFeatureBundles(price, assumptions);
    const targetFeatureCount = Math.max(1, sortedFeatures.length - i * Math.floor(sortedFeatures.length / tierCount));

    const best = bundles.find(b => b.featureCount <= targetFeatureCount) ?? bundles[0];
    return { price, features: best.features };
  });
}

export function SuggestTiersButton() {
  const [appliedMessage, setAppliedMessage] = useState<string | null>(null);
  const previousPackagesRef = useRef<{ enabledFeatures: string[] }[] | null>(null);

  const assumptions = useSimulatorStore(s => s.assumptions);
  const packages = useSimulatorStore(s => s.packages);
  const setPackageConfig = useSimulatorStore(s => s.setPackageConfig);
  const resetToDefaults = useSimulatorStore(s => s.resetToDefaults);

  const handleSuggest = useCallback(() => {
    previousPackagesRef.current = packages.map(p => ({ enabledFeatures: [...p.enabledFeatures] }));
    const suggestions = computeSuggestions(packages.length, assumptions);
    suggestions.forEach((s, i) => {
      if (i < packages.length) {
        setPackageConfig(i, s.price, s.features);
      }
    });
    const changes = suggestions.slice(0, packages.length).map((s, i) => {
      const prev = previousPackagesRef.current?.[i]?.enabledFeatures.length ?? 0;
      return prev !== s.features.length
        ? `${packages[i].name}: ${prev} → ${s.features.length} features @ ${formatPrice(s.price)}`
        : null;
    }).filter(Boolean);
    setAppliedMessage(changes.length > 0 ? changes.join('. ') : 'No change — suggested tiers match current packages.');
    setTimeout(() => setAppliedMessage(null), 6000);
  }, [packages, assumptions, setPackageConfig]);

  const handleFromDefaults = useCallback(() => {
    resetToDefaults();
    setTimeout(() => {
      const state = useSimulatorStore.getState();
      const suggestions = computeSuggestions(state.packages.length, state.assumptions);
      suggestions.forEach((s, i) => {
        if (i < state.packages.length) {
          state.setPackageConfig(i, s.price, s.features);
        }
      });
      setAppliedMessage('Reset to defaults and applied tier suggestions.');
      setTimeout(() => setAppliedMessage(null), 6000);
    }, 0);
  }, [resetToDefaults]);

  return (
    <div className="mb-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={handleSuggest}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all border border-chart-teal/30 text-chart-teal hover:bg-chart-teal/10"
        >
          <Wand2 size={15} />
          Suggest Tiers ({packages.length} packages)
        </button>
        <button
          onClick={handleFromDefaults}
          className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-border text-secondary hover:text-primary hover:bg-surface"
        >
          <RotateCcw size={14} />
          Reset &amp; Suggest
        </button>
        <InfoButton helpKey="suggestTiers" size={13} />
        {appliedMessage && (
          <span className="text-xs text-muted animate-pulse">
            {appliedMessage}
          </span>
        )}
      </div>
    </div>
  );
}
